import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal, flushSync } from "react-dom";
import { t } from "../lib/sprache";

/** „Mehr“: kleines Menü über der Seite (Popover), damit die rechts fixierte Spalte es nicht abschneidet */
export function Mehr({ children, label }: { children: (close: () => void) => ReactNode; label?: string }) {
  const id = useId().replace(/:/g, "");
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  // erst im Browser (Portal braucht document; die Seite wird in Tests auch auf dem Server gezeichnet)
  const [bereit, setBereit] = useState(false);
  useEffect(() => setBereit(true), []);
  // K-553: Inhalt nur, solange das Menü offen ist; sonst lagen die Punkte aller Zeilen (versteckt) im Dokument und eine Suche
  // nach „Im Chat teilen“ fand den Link einer anderen Zeile
  const [offen, setOffen] = useState(false);
  useEffect(() => {
    const p = pop.current;
    if (!p) return;
    const place = () => {
      const r = btn.current?.getBoundingClientRect();
      if (!r) return;
      const w = Math.max(p.offsetWidth, 200);
      const h = p.offsetHeight || 120;
      p.style.left = `${Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8))}px`;
      p.style.top = `${r.bottom + 4 + h > window.innerHeight - 8 ? Math.max(8, r.top - h - 4) : r.bottom + 4}px`;
    };
    const zustand = (e: Event) => (e as Event & { newState?: string }).newState;
    // vor dem Öffnen den Inhalt zeichnen (synchron), damit Größe und Lage schon stimmen; nach dem Schließen leeren
    const vorher = (e: Event) => { if (zustand(e) === "open") { flushSync(() => setOffen(true)); place(); } };
    const nachher = (e: Event) => { if (zustand(e) === "open") place(); else setOffen(false); };
    p.addEventListener("beforetoggle", vorher);
    p.addEventListener("toggle", nachher);
    return () => { p.removeEventListener("beforetoggle", vorher); p.removeEventListener("toggle", nachher); };
  }, [bereit]);
  const close = () => { try { pop.current?.hidePopover(); } catch { /* schon zu */ } };
  return (
    <>
      {/* label: eindeutige Beschriftung je Zeile (z. B. „Aktionen für Stufe 2“), sonst „Mehr“ */}
      <button ref={btn} type="button" className="btn tk-inv-mehr" popoverTarget={`mehr${id}`} aria-haspopup="menu" title={label ?? t("Mehr", "More")}>
        <span aria-hidden="true">⋯</span><span className="sr-only">{label ?? t("Mehr", "More")}</span>
      </button>
      {/* am Ende des Dokuments, damit die Knopfregeln der Tabelle nicht greifen */}
      {bereit && createPortal(<div ref={pop} id={`mehr${id}`} popover="auto" role="menu" className="tk-inv-menue">{offen ? children(close) : null}</div>, document.body)}
    </>
  );
}
